import { isEmpty } from "lodash/fp";

import useMeetingOverage, {
  UseMeetingOverage,
} from "@app/analytics/hooks/meetings/useMeetingOverage";
import useMetricsQuery from "@app/analytics/hooks/metrics/useMetricsQuery";
import * as Filters from "@app/analytics/meetops/filters";
import { MeetOpsQuery, QueryMetric } from "@app/analytics/meetops/types";
import {
  ApiTimekeeperResponse,
  TeamTimekeeperData,
  TimekeeperData,
} from "@app/analytics/WorkspaceOverview/TimeKeeper/types";
import { useWorkspaceOverview } from "@app/analytics/WorkspaceOverview/WorkspaceOverviewProvider";
import { EnterpriseWorkspaceTeam } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

const METRICS: QueryMetric[] = ["meetingLateStartPercent", "avgMeetingLateBy"];

const getTimeKeeperQuery = ({
  dateRanges,
  dimensions = [],
}: {
  dateRanges: MeetOpsQuery["dateRanges"];
  dimensions?: MeetOpsQuery["dimensions"];
}): MeetOpsQuery => ({
  dateRanges,
  dimensions,
  metrics: METRICS,
  filters: [Filters.notNullTeams()],
  people: "everyone",
});

const getTableData = (
  teams: EnterpriseWorkspaceTeam[],
  data: ApiTimekeeperResponse[],
  meetingOverages: UseMeetingOverage["byTeam"]
): TeamTimekeeperData[] => {
  if (isEmpty(data)) return [];

  const teamsData = [];

  for (const team of teams) {
    const teamData = data.find(
      (t: ApiTimekeeperResponse) => t.teamId === team.id
    );

    if (!teamData) continue;

    const overageData = meetingOverages[team.id];

    const newData = {
      ...team,
      percentMeetingsWithLateStart: teamData.meetingLateStartPercent.value ?? 0,
      rawNumMinsLateStart: (teamData.avgMeetingLateBy.value ?? 0) / 60,
      percentMeetingsWithOverage: overageData?.percent ?? 0,
      numMinsMeetingOverage: overageData?.time ?? 0,
    };
    teamsData.push(newData);
  }

  return teamsData;
};

const getFormattedWorkspaceAverages = (
  data: ApiTimekeeperResponse,
  workspaceMeetingOverage?: { percent: number; time: number }
): TimekeeperData => ({
  percentMeetingsWithLateStart: data?.meetingLateStartPercent?.value ?? 0,
  rawNumMinsLateStart: (data?.avgMeetingLateBy?.value ?? 0) / 60,
  percentMeetingsWithOverage: workspaceMeetingOverage?.percent ?? 0,
  numMinsMeetingOverage: workspaceMeetingOverage?.time ?? 0,
});

const useTimeKeeper = () => {
  const { queryDateRanges } = useWorkspaceOverview();
  const { workspace } = useWorkspace();

  const timekeeperQuery = useMetricsQuery(
    getTimeKeeperQuery({
      dateRanges: queryDateRanges,
      dimensions: ["teamId"],
    }),
    {
      queryKey: ["timeKeeper", workspace.id],
      staleTime: 30 * 60 * 1000,
    }
  );

  const timekeeperQueryResults = timekeeperQuery?.data?.results[0].data ?? [];

  const workspaceAverageQuery = useMetricsQuery(
    getTimeKeeperQuery({ dateRanges: queryDateRanges }),
    {
      queryKey: ["timeKeeperWorkspaceAverage", workspace.id],
      staleTime: 30 * 60 * 1000,
    }
  );

  const workspaceAverageData =
    workspaceAverageQuery?.data?.results[0].data[0] ?? {};

  const meetingOverages = useMeetingOverage({
    dateRanges: queryDateRanges,
    includeTeamBreakout: true,
  });

  const refetchAll = () => {
    if (timekeeperQuery.isError) timekeeperQuery.refetch();
    if (workspaceAverageQuery.isError) workspaceAverageQuery.refetch();
    if (meetingOverages.isError) meetingOverages.refetch();
  };

  return {
    isInitialLoading:
      timekeeperQuery.isInitialLoading ||
      workspaceAverageQuery.isInitialLoading ||
      meetingOverages.isInitialLoading,
    isError:
      timekeeperQuery.isError ||
      workspaceAverageQuery.isError ||
      meetingOverages.isError,
    tableData: getTableData(
      workspace.teams,
      timekeeperQueryResults,
      meetingOverages.byTeam
    ),
    workspaceAverages: getFormattedWorkspaceAverages(
      workspaceAverageData,
      meetingOverages.average
    ),
    refetch: refetchAll,
  };
};

export default useTimeKeeper;
